import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  CartesianGrid,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { labelListStyle } from '../../constants/line-label-styles';
import { DeleteUndefinedAttributes } from '../../data-transformer/common-commands/delete-undefined-attributes';
import { DataExecutor } from '../../data-transformer/data-executor';
import { getStatusColor } from '../../helpers/getStatusColor';
import { type DataKPI, useChartKPIQuery } from '../../redux/config/chart-data';
import type { WidgetConfigurationData } from '../../redux/config/widgetApi';
import { getWidgets } from '../../redux/slices/layout-tabs-slice';
import CustomToolTip from '../CustomToolTip/CustomToolTip';
import ChartGenericWrapper from '../common/chart-generic-wrapper';
import { defaultLegendWrapperStyle, renderLegend } from '../common/custom-legend.component';
import { XAxisLineChart } from '../common/custom-X-axis.component';
import { ActiveDot } from '../common/line-active-dot.component';
import { getDotWithColor } from '../common/line-dot-base-props';
import { compactNumberFormatter } from '../common/tick-formatter';
import type { CustomColorProps } from '../WidgetSettingWindow/components/config-components.interfaces';
import type { ChartProps } from './chart.interface';
import type { LegendPayloadOnClick } from './chart-events';
import { intervalCounter } from './generalFunctions/general-functions';
import { generateTicks } from './generalFunctions/generate-ticks';
import {
  filterChartValueByLegend,
  getCustomKpiDomainValue,
  getCustomYScaleDomainValue,
} from './generalFunctions/get-domaincharts';
import transformDataChartKPI from './generalFunctions/transform-data-kpi';
import { useXAxisTopSettings } from './hooks/use-x-axis-top-settings';
import useInvalidateChartResponse from './hooks/useInvalidateChartResponse';
import { useKpiConfigs } from './hooks/useKPIConfigs';
import { useFRConfigs } from './hooks/useRollingFRConfigs';
import { useTRIFRConfigs } from './hooks/useRollingTRIFRConfigs';
import useStartEndDates from './hooks/useStartEndDates';

export default function OdinChartKPI({ widgetId, layoutProps, activateAnimation }: ChartProps) {
  const [legendProps, setLegendProps] = useState<Record<string | keyof DataKPI, boolean>>({});
  const [transformedData, setTransformedData] = useState<DataKPI[]>();
  const [colors, setCustomColors] = useState<CustomColorProps[]>([]);
  const [configurationData, setConfigurationData] = useState<WidgetConfigurationData>();

  const { startDate, endDate } = useStartEndDates();
  const kpiConfigs = useKpiConfigs(configurationData);
  const trifrConfig = useTRIFRConfigs(configurationData);
  const frConfig = useFRConfigs(configurationData);
  const xAxisSettings = useXAxisTopSettings('name');

  const widgets = useSelector(getWidgets);

  const {
    data: widgetChartData,
    isLoading,
    isFetching: isWidgetChartDataFetching,
    isError,
    error,
  } = useChartKPIQuery({
    widgetId,
    from: startDate,
    to: endDate,
  });

  useEffect(() => {
    if (widgets) {
      setConfigurationData(widgets.find((widget) => +widget.widget.id === +widgetId)?.widget);
    }
  }, [widgets, widgetId]);

  useEffect(() => {
    const commandExecutor = new DataExecutor();
    if (configurationData && widgetChartData) {
      const transformedDataResponse = transformDataChartKPI(configurationData, widgetChartData);
      const removedUndefinedResponse = commandExecutor.execute(
        new DeleteUndefinedAttributes<DataKPI>(),
        transformedDataResponse,
        configurationData
      );
      setTransformedData(removedUndefinedResponse);
    }
  }, [widgetChartData, configurationData]);

  const selectItem = (e: LegendPayloadOnClick) => {
    const key: string = (e.dataKey as string) || (e.value as string) || '';
    setLegendProps({
      ...legendProps,
      [key]: !legendProps[key],
    });
  };

  useMemo(() => {
    const newColors: CustomColorProps[] = [];
    if (kpiConfigs.available) {
      newColors.push({ label: kpiConfigs.caption, color: kpiConfigs.lineColor });
    }
    if (trifrConfig.available) {
      newColors.push({ label: trifrConfig.caption, color: trifrConfig.lineColor });
    }
    setCustomColors(newColors);
  }, [kpiConfigs, trifrConfig]);

  const handleRefetch = useInvalidateChartResponse(widgetId);

  const filteredTransformData = useMemo(() => {
    return filterChartValueByLegend(transformedData, legendProps);
  }, [transformedData, legendProps]);

  const domainScale: [number, number] | undefined = useMemo(() => {
    if (filteredTransformData && configurationData) {
      const { max: maxValue, min: minValue } = getCustomKpiDomainValue(filteredTransformData, configurationData);

      const widgetConfig = configurationData.widgetConfig;
      if (widgetConfig) {
        return getCustomYScaleDomainValue({
          minChartValue: minValue,
          maxChartValue: maxValue,
          maxScaleValue: widgetConfig.yScaleMax,
          minScaleValue: widgetConfig.yScaleMin,
        });
      } else {
        return [minValue, maxValue ? maxValue : 2];
      }
    }
    return undefined;
  }, [filteredTransformData, configurationData]);

  const ticks: number[] | undefined = generateTicks(domainScale);

  const getLabelList = useCallback(
    (dataKey: string) => <LabelList dataKey={dataKey} position="top" dy={-5} style={labelListStyle} />,
    []
  );

  return (
    <ChartGenericWrapper
      isError={isError}
      error={error}
      isFetching={isWidgetChartDataFetching}
      isLoading={isLoading}
      refetch={handleRefetch}
      widgetId={widgetId}
      widgetConfigurationData={configurationData && { ...configurationData, colors, layoutProps }}
      widgetChartData={widgetChartData}
      renderNoDataByDefault={!isWidgetChartDataFetching && !transformedData?.length}
    >
      {configurationData && widgetChartData && transformedData && transformedData.length > 0 && (
        <ResponsiveContainer height="99%">
          <LineChart
            data={transformedData}
            margin={{
              top: 20,
              right: 20,
              left: 20,
              bottom: 20,
            }}
            layout="horizontal"
            id={`${widgetId}`}
          >
            <Tooltip content={<CustomToolTip />} cursor={false} isAnimationActive={false} />
            <>
              <CartesianGrid vertical={false} horizontal strokeDasharray={undefined} />
              <XAxis {...xAxisSettings} interval={intervalCounter<DataKPI>(transformedData)} tick={XAxisLineChart} />
              <YAxis
                type="number"
                domain={domainScale}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                label={{
                  value: 'Frequency Rate',
                  angle: -90,
                  dx: -40,
                  position: 'center',
                }}
                tickFormatter={compactNumberFormatter}
                orientation="left"
                tick
                ticks={ticks}
                allowDataOverflow={false}
              />
              <Legend
                formatter={renderLegend}
                verticalAlign="bottom"
                wrapperStyle={defaultLegendWrapperStyle}
                onClick={selectItem}
              />

              {/* KPI */}
              {kpiConfigs.available && widgetChartData.kpi && (
                <Line
                  name={kpiConfigs.caption}
                  connectNulls
                  type="linear"
                  dataKey="value_KPI"
                  strokeWidth={2}
                  stroke={kpiConfigs.lineColor}
                  activeDot={<ActiveDot item={kpiConfigs.caption} />}
                  fill={kpiConfigs.lineColor}
                  hide={legendProps.value_KPI}
                  isAnimationActive={activateAnimation}
                  dot={getDotWithColor(kpiConfigs.lineColor)}
                >
                  {getLabelList('value_KPI')}
                </Line>
              )}

              {/* rollingTRIFR */}
              {trifrConfig.available && (
                <Line
                  name={trifrConfig.caption}
                  type="linear"
                  dataKey="value_TRIFR"
                  strokeWidth={2}
                  stroke={trifrConfig.lineColor}
                  activeDot={<ActiveDot item={trifrConfig.caption} />}
                  fill={trifrConfig.lineColor}
                  hide={legendProps.value_TRIFR}
                  isAnimationActive={activateAnimation}
                  dot={getDotWithColor(trifrConfig.lineColor)}
                >
                  {trifrConfig.displayValues && getLabelList('value_TRIFR')}
                </Line>
              )}

              {/* rollingFR */}
              {widgetChartData.rollingFR &&
                widgetChartData.rollingFR.map((item, index) => (
                  <Line
                    key={index}
                    name={frConfig.legend[index]}
                    type="linear"
                    dataKey="value_FR"
                    strokeWidth={2}
                    stroke={frConfig.colors[index]}
                    fill={frConfig.colors[index]}
                    hide={legendProps.value_FR}
                    isAnimationActive={activateAnimation}
                    dot={getDotWithColor(frConfig.colors[index])}
                    activeDot={<ActiveDot item={frConfig.legend[index]} />}
                  >
                    {frConfig.displayValues && getLabelList('value_FR')}
                  </Line>
                ))}

              {/* rollingSiteTRIFR */}
              {widgetChartData.rollingSiteTRIFR &&
                Object.keys(widgetChartData.rollingSiteTRIFR).map((item, index: number) => (
                  <Line
                    key={item}
                    name={item}
                    dataKey={item}
                    strokeWidth={2}
                    type="linear"
                    stroke={getStatusColor(index)}
                    fill={getStatusColor(index)}
                    hide={legendProps[item]}
                    activeDot={<ActiveDot item={item} />}
                    isAnimationActive={activateAnimation}
                    dot={getDotWithColor(getStatusColor(index))}
                  >
                    {getLabelList(item)}
                  </Line>
                ))}
            </>
          </LineChart>
        </ResponsiveContainer>
      )}
    </ChartGenericWrapper>
  );
}
